'use client';

import type { ChangeEvent, FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type RangeSliderProps = {
  min: number;
  max: number;
  step?: number;
  values: [number, number];
  onChange: (values: [number, number]) => void;
};

export const RangeSlider: FC<RangeSliderProps> = ({
  min,
  max,
  step = 1,
  values,
  onChange,
}) => {
  const [minValue, setMinValue] = useState<number>(values[0]);
  const [maxValue, setMaxValue] = useState<number>(values[1]);
  const [dragging, setDragging] = useState<'min' | 'max' | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Update internal state when external values change
  useEffect(() => {
    setMinValue(values[0]);
    setMaxValue(values[1]);
  }, [values]);

  // Helper: Convert clientX position to a slider value
  const getValueFromPosition = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) {
        return min;
      }
      const { left, width } = sliderRef.current.getBoundingClientRect();
      let percent = (clientX - left) / width;
      percent = Math.min(Math.max(percent, 0), 1);
      const rawVal = percent * (max - min) + min;
      // Round to nearest step
      return Math.round(rawVal / step) * step;
    },
    [min, max, step],
  );

  // Mouse move handler for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (dragging && sliderRef.current) {
        const newValue = getValueFromPosition(e.clientX);
        if (dragging === 'min') {
          const clamped = Math.min(newValue, maxValue - step);
          setMinValue(clamped);
          onChange([clamped, maxValue]);
        } else if (dragging === 'max') {
          const clamped = Math.max(newValue, minValue + step);
          setMaxValue(clamped);
          onChange([minValue, clamped]);
        }
      }
    };

    const handleMouseUp = () => {
      if (dragging) {
        setDragging(null);
      }
    };

    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, getValueFromPosition, minValue, maxValue, step, onChange]);

  // Calculate positions (percentages) for the handles
  const minPos = ((minValue - min) / (max - min)) * 100;
  const maxPos = ((maxValue - min) / (max - min)) * 100;

  // Determine cursor style for the handles
  const handleCursor = dragging ? 'grabbing' : 'grab';

  // Handlers for number inputs
  const handleInputMinChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newVal = Math.min(Number(e.target.value), maxValue - step);
    setMinValue(newVal);
    onChange([newVal, maxValue]);
  };

  const handleInputMaxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newVal = Math.max(Number(e.target.value), minValue + step);
    setMaxValue(newVal);
    onChange([minValue, newVal]);
  };

  return (
    <div
      className="wrapper relative flex flex-col items-center"
    >
      {/* Min and max values */}
      <div className="w-full mb-2 text-sm font-medium flex flex-row justify-between">
        <span>
          Min:
          {' '}
          {minValue}
        </span>
        <span>
          Max:
          {' '}
          {maxValue}
        </span>
      </div>

      {/* Slider Track and Handles */}
      <div
        ref={sliderRef}
        className="control-wrapper relative w-full h-[16px]"
      >
        {/* Rail (full track) */}
        <div
          className="rail absolute w-full h-[6px] bg-gray-300 rounded-[3px] top-1/2 -translate-y-1/2"
        >
        </div>
        {/* Active Track */}
        <div
          className="inner-rail absolute h-[6px] bg-black opacity-50 top-1/2 -translate-y-1/2"
          style={{
            left: `${minPos}%`,
            width: `${maxPos - minPos}%`,
          }}
        >
        </div>
        {/* Minimum Handle */}
        <div
          role="slider"
          tabIndex={0}
          aria-valuemin={min}
          aria-valuemax={maxValue - step}
          aria-valuenow={minValue}
          aria-label="Minimum value"
          className="control absolute w-4 h-4 bg-black rounded-full z-10 top-1/2 -translate-y-1/2 ml-[-8px]"
          style={{ left: `${minPos}%`, cursor: handleCursor }}
          onMouseDown={() => setDragging('min')}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
              // Increase the minimum value
              const newVal = Math.min(maxValue - step, minValue + step);
              setMinValue(newVal);
              onChange([newVal, maxValue]);
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
              // Decrease the minimum value
              const newVal = Math.max(min, minValue - step);
              setMinValue(newVal);
              onChange([newVal, maxValue]);
            }
          }}
        >
        </div>
        {/* Maximum Handle */}
        <div
          role="slider"
          tabIndex={0}
          aria-valuemin={minValue + step}
          aria-valuemax={max}
          aria-valuenow={maxValue}
          aria-label="Maximum value"
          className="control absolute w-4 h-4 bg-black rounded-full z-10 top-1/2 -translate-y-1/2 ml-[-8px]"
          style={{ left: `${maxPos}%`, cursor: handleCursor }}
          onMouseDown={() => setDragging('max')}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
              // Increase the maximum value
              const newVal = Math.min(max, maxValue + step);
              setMaxValue(newVal);
              onChange([minValue, newVal]);
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
              // Decrease the maximum value
              const newVal = Math.max(minValue + step, maxValue - step);
              setMaxValue(newVal);
              onChange([minValue, newVal]);
            }
          }}
        >
        </div>
      </div>

      {/* Number Inputs for Precision */}
      <div className="flex items-center justify-between space-x-2 mt-4 w-full">
        <input
          type="number"
          className="w-1/2 p-1 border border-gray-300 rounded text-center"
          min={min}
          max={maxValue - step}
          step={step}
          value={minValue}
          onChange={handleInputMinChange}
        />
        <input
          type="number"
          className="w-1/2 p-1 border border-gray-300 rounded text-center"
          min={minValue + step}
          max={max}
          step={step}
          value={maxValue}
          onChange={handleInputMaxChange}
        />
      </div>
    </div>
  );
};

export default RangeSlider;
